import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, FileText, Users, ShieldAlert, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { adminApi, type AdminCustomerDetail } from '@/services/adminApi';

/**
 * AdminCustomerDetailPage — single company view used in the Backoffice
 * MVP. Pulls the full company doc + cheap KPI counts in one round trip
 * via `/admin/customers/:cid` and the audit feed via the dedicated
 * `/admin/customers/:cid/audit` endpoint.
 */
export default function AdminCustomerDetailPage() {
  const { companyId = '' } = useParams<{ companyId: string }>();
  const [data, setData] = useState<AdminCustomerDetail | null>(null);
  const [audit, setAudit] = useState<Array<{
    id: string;
    adminEmail: string | null;
    method: string;
    route: string;
    kind: 'read' | 'write';
    at?: string;
  }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Action dialog open state
  const [editOpen, setEditOpen] = useState(false);
  const [pendingSuspend, setPendingSuspend] = useState(false);
  const [pendingUnsuspend, setPendingUnsuspend] = useState(false);
  const [pendingSoftDelete, setPendingSoftDelete] = useState(false);
  const [pendingPermDelete, setPendingPermDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [detail, auditResp] = await Promise.all([
          adminApi.customers.detail(companyId),
          adminApi.customers.audit(companyId, 30).catch(() => ({ items: [], count: 0 })),
        ]);
        if (cancelled) return;
        setData(detail);
        setAudit(auditResp.items.map((i) => ({
          id: i.id,
          adminEmail: i.adminEmail,
          method: i.method,
          route: i.route,
          kind: i.kind,
          at: i.at,
        })));
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to="/admin/customers"><ArrowLeft className="h-4 w-4" /> Back to customers</Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-md bg-primary/10 flex items-center justify-center text-xl font-semibold text-primary overflow-hidden">
          {data.logoUrl ? (
            <img src={data.logoUrl} alt={data.publicName} className="h-full w-full object-cover" />
          ) : (
            <span>{(data.publicName || data.name)[0]}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold">
            {data.publicName || data.name}
            {!data.isActive && <span className="ml-2 inline-block text-xs px-2 py-0.5 rounded-md bg-destructive/10 text-destructive align-middle">Suspended</span>}
          </h1>
          <p className="text-sm text-muted-foreground">
            {data.segment} · plan {data.billing?.planId ?? '—'} · {data.isActive ? 'active' : 'inactive'}
          </p>
          <p className="text-xs text-muted-foreground mt-1 font-mono">{data.id}</p>
        </div>
        <Button onClick={() => setEditOpen(true)} variant="outline" size="sm">Edit company</Button>
      </div>

      {/* KPI grid + drill-down links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard
          label="Job positions"
          value={data.kpi.positionsCount}
          icon={<Briefcase className="h-4 w-4" />}
        />
        <KpiCard
          label="Postings"
          value={data.kpi.postingsCount ?? 0}
          icon={<Briefcase className="h-4 w-4" />}
          href={`/admin/postings?companyId=${data.id}`}
        />
        <KpiCard
          label="Applications"
          value={data.kpi.applicationsCount}
          icon={<FileText className="h-4 w-4" />}
          href={`/admin/applications?companyId=${data.id}`}
        />
        <KpiCard label="Members" value={data.kpi.membersCount} icon={<Users className="h-4 w-4" />} />
      </div>

      {/* Billing + Trial summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Billing</h2>
              <Button asChild size="sm" variant="outline">
                <Link to={`/admin/customers/${companyId}/subscription`}>Open subscription detail</Link>
              </Button>
            </div>
            <Row k="Plan" v={data.billing?.planId ?? '—'} />
            <Row k="Plan updated" v={data.billing?.planUpdatedAt ? new Date(data.billing.planUpdatedAt).toLocaleString('en-GB') : '—'} />
            {data.parentAgencyId && <Row k="Parent agency" v={data.parentAgencyId} />}
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Trial</h2>
            <Row k="Status" v={data.trial?.status ?? '—'} />
            <Row k="Started" v={data.trial?.startedAt ? new Date(data.trial.startedAt).toLocaleString('en-GB') : '—'} />
            <Row k="Ends" v={data.trial?.endsAt ? new Date(data.trial.endsAt).toLocaleString('en-GB') : '—'} />
          </CardContent>
        </Card>
      </div>

      {/* Owners */}
      <Card className="border-border/50">
        <CardContent className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Admin owners</h2>
          {data.owners.length === 0 ? (
            <p className="text-sm text-muted-foreground">No admin members linked.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.owners.map((o, i) => (
                <li key={(o.uid ?? '') + i} className="text-sm">
                  <span className="font-medium">{o.displayName ?? o.email ?? '(unknown)'}</span>
                  {o.email && <span className="text-muted-foreground"> · {o.email}</span>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-destructive/30">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-destructive">
            Danger zone
          </h2>
          <p className="text-xs text-muted-foreground">
            Suspending the company blocks every recruiter-side write. Soft delete keeps the data
            but hides the company from the recruiter UI. Permanent delete is irreversible —
            sub-collections (positions, applications) will be orphaned for cleanup.
          </p>
          <div className="flex flex-wrap gap-2">
            {data.isActive ? (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={() => setPendingSuspend(true)}
              >
                <ShieldOff className="h-3.5 w-3.5" /> Suspend
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => setPendingUnsuspend(true)}
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Unsuspend
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => setPendingSoftDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5" /> Soft delete
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => setPendingPermDelete(true)}
            >
              <ShieldAlert className="h-3.5 w-3.5" /> Permanent delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit feed */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border/50">
            <h2 className="text-sm font-semibold">Recent admin activity on this company</h2>
          </div>
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">No admin activity recorded for this company yet.</p>
          ) : (
            <ul className="divide-y divide-border/30">
              {audit.map((row) => (
                <li key={row.id} className="px-5 py-2 flex items-center gap-3">
                  <span
                    className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                      row.kind === 'write'
                        ? 'bg-destructive/10 text-destructive'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {row.method}
                  </span>
                  <span className="text-xs text-muted-foreground flex-1 min-w-0 truncate font-mono">{row.route}</span>
                  <span className="text-xs text-muted-foreground">{row.adminEmail ?? '—'}</span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {row.at ? new Date(row.at).toLocaleString('en-GB') : '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ─── Dialogs ──────────────────────────────────────────────────────── */}
      <EditCompanyDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        company={data}
        onSaved={() => { setEditOpen(false); refresh(); }}
      />
      <ReasonConfirmDialog
        open={pendingSuspend}
        onOpenChange={setPendingSuspend}
        title="Suspend company?"
        description={`This blocks every recruiter-side write on ${data.publicName || data.name}. Reads stay available so users can still see their data and contact support.`}
        confirmLabel="Suspend"
        destructive
        onConfirm={async (reason) => {
          await adminApi.customers.suspend(companyId, reason);
          toast.success('Company suspended');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingUnsuspend}
        onOpenChange={setPendingUnsuspend}
        title="Unsuspend company?"
        description={`This restores write access for ${data.publicName || data.name}.`}
        confirmLabel="Unsuspend"
        onConfirm={async (reason) => {
          await adminApi.customers.unsuspend(companyId, reason);
          toast.success('Company restored');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingSoftDelete}
        onOpenChange={setPendingSoftDelete}
        title="Soft delete company?"
        description={`This hides ${data.publicName || data.name} from the recruiter UI by marking it inactive. Sub-collections are preserved.`}
        confirmLabel="Soft delete"
        destructive
        onConfirm={async (reason) => {
          await adminApi.customers.softDelete(companyId, reason);
          toast.success('Company soft-deleted');
          refresh();
        }}
      />
      <PermanentDeleteDialog
        open={pendingPermDelete}
        onOpenChange={setPendingPermDelete}
        company={data}
        onDeleted={() => {
          toast.success('Company permanently deleted');
          // Navigate back — there is nothing left to refresh.
          window.location.href = '/admin/customers';
        }}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
  href,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  href?: string;
}) {
  const inner = (
    <CardContent className="p-5 flex items-center justify-between">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold tabular-nums">{value.toLocaleString('en-US')}</p>
      </div>
      <span className="text-muted-foreground">{icon}</span>
    </CardContent>
  );
  if (href) {
    return (
      <Card className="border-border/50 hover:border-primary/40 transition-colors">
        <Link to={href} className="block">{inner}</Link>
      </Card>
    );
  }
  return <Card className="border-border/50">{inner}</Card>;
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-muted-foreground w-32 shrink-0">{k}</span>
      <span className="font-medium break-all">{v}</span>
    </div>
  );
}

function EditCompanyDialog({
  open,
  onOpenChange,
  company,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  company: AdminCustomerDetail;
  onSaved: () => void;
}) {
  const initial = useMemo(() => ({
    name: company.name ?? '',
    publicName: company.publicName ?? '',
    description: company.description ?? '',
    employeeCount: typeof company.employeeCount === 'number' ? String(company.employeeCount) : '',
  }), [company]);
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial); setReason(''); }, [initial, open]);

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const payload: Parameters<typeof adminApi.customers.update>[1] = { reason };
      if (form.name !== initial.name) payload.name = form.name;
      if (form.publicName !== initial.publicName) payload.publicName = form.publicName;
      if (form.description !== initial.description) payload.description = form.description;
      if (form.employeeCount !== initial.employeeCount) {
        const n = parseInt(form.employeeCount, 10);
        if (Number.isFinite(n)) payload.employeeCount = n;
      }
      const keys = Object.keys(payload).filter((k) => k !== 'reason');
      if (keys.length === 0) {
        toast.info('No changes to save.');
        return;
      }
      await adminApi.customers.update(company.id, payload);
      toast.success('Company updated');
      onSaved();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit company</DialogTitle>
          <DialogDescription>
            Updating {company.publicName || company.name}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="company-name">Legal name</Label>
            <Input id="company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="public-name">Public name</Label>
            <Input id="public-name" value={form.publicName} onChange={(e) => setForm({ ...form, publicName: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="employees">Employee count</Label>
            <Input id="employees" type="number" min={0} value={form.employeeCount} onChange={(e) => setForm({ ...form, employeeCount: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reason-edit-company">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="reason-edit-company" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you making this change?" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()}>{busy ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {destructive ? <ShieldAlert className="h-4 w-4 text-destructive" /> : <ShieldCheck className="h-4 w-4" />}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reason-customer-confirm">Reason <span className="text-destructive">*</span></Label>
          <Textarea
            id="reason-customer-confirm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you doing this?"
            rows={2}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={busy || !reason.trim()}
            className={destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {busy ? 'Working...' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PermanentDeleteDialog({
  open,
  onOpenChange,
  company,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  company: AdminCustomerDetail;
  onDeleted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReason(''); setConfirmText(''); } }, [open]);

  const expected = company.id;
  const canSubmit = reason.trim() && confirmText.trim() === expected && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await adminApi.customers.permanentDelete(company.id, reason.trim(), confirmText.trim());
      onDeleted();
    } catch (err) {
      toast.error((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-destructive" />
            Permanently delete company?
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              This will permanently delete <strong>{company.publicName || company.name}</strong>.
              Sub-collections (positions, applications, members) will be orphaned.
            </span>
            <span className="block text-xs text-destructive">This action is irreversible.</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="reason-perm-delete">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="reason-perm-delete" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-perm">Type the company id to confirm: <span className="font-mono text-foreground">{expected}</span></Label>
            <Input id="confirm-perm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="font-mono" />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={!canSubmit}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {busy ? 'Deleting...' : 'Delete forever'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
